import { useState } from "react";
import type { CustomerContact } from "../types";
import { useCustomerContacts } from "../api";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { MapPin, User, Phone, Mail, Star } from "lucide-react";
import { cn } from "@/lib/utils";

interface AddressBookSelectorProps {
  customerId?: string;
  onSelect: (contact: CustomerContact) => void;
  selectedContactId?: string;
  label?: string;
  className?: string;
  showDeliveryInstructions?: boolean;
}

export function AddressBookSelector({
  customerId,
  onSelect,
  selectedContactId,
  label = "Select from Address Book",
  className,
  showDeliveryInstructions = true,
}: AddressBookSelectorProps) {
  const [selectedId, setSelectedId] = useState(selectedContactId);

  const { data, isLoading } = useCustomerContacts(customerId || "", {});

  if (!customerId) {
    return (
      <div className={cn("text-sm text-muted-foreground", className)}>
        Please select a customer first to use address book
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className={cn("text-sm text-muted-foreground", className)}>
        Loading contacts...
      </div>
    );
  }

  const contacts = data?.contacts ?? [];
  const selectedContact = contacts.find((c) => c.id === selectedId);

  if (!contacts.length) {
    return (
      <div className={cn("text-sm text-muted-foreground", className)}>
        No saved contacts found for this customer
      </div>
    );
  }

  const handleSelect = (contactId: string) => {
    setSelectedId(contactId);
    const contact = contacts.find((c) => c.id === contactId);
    if (contact) {
      onSelect(contact);
    }
  };

  return (
    <div className={cn("space-y-3", className)}>
      <Select value={selectedId} onValueChange={handleSelect}>
        <SelectTrigger>
          <SelectValue placeholder={label} />
        </SelectTrigger>
        <SelectContent>
          {contacts.map((contact) => (
            <SelectItem key={contact.id} value={contact.id}>
              <div className="flex items-center gap-2">
                {contact.isDefault && (
                  <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                )}
                <span>{contact.contactName}</span>
                {contact.label && (
                  <span className="text-xs text-muted-foreground">
                    ({contact.label})
                  </span>
                )}
              </div>
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {selectedContact && (
        <Card>
          <CardContent className="pt-4 space-y-2">
            <div className="flex items-start gap-2 text-sm">
              <User className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <div className="font-medium">{selectedContact.contactName}</div>
                {selectedContact.contactCompany && (
                  <div className="text-muted-foreground">
                    {selectedContact.contactCompany}
                  </div>
                )}
              </div>
            </div>

            {(selectedContact.contactPhone || selectedContact.contactEmail) && (
              <div className="space-y-1">
                {selectedContact.contactPhone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedContact.contactPhone}</span>
                  </div>
                )}
                {selectedContact.contactEmail && (
                  <div className="flex items-center gap-2 text-sm">
                    <Mail className="h-4 w-4 text-muted-foreground" />
                    <span>{selectedContact.contactEmail}</span>
                  </div>
                )}
              </div>
            )}

            <div className="flex items-start gap-2 text-sm pt-2 border-t">
              <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground" />
              <div>
                <div>{selectedContact.addressLine1}</div>
                {selectedContact.addressLine2 && (
                  <div>{selectedContact.addressLine2}</div>
                )}
                <div>
                  {selectedContact.addressCity},{" "}
                  {selectedContact.addressProvince}{" "}
                  {selectedContact.addressPostalCode}
                </div>
                <div>{selectedContact.addressCountry}</div>
              </div>
            </div>

            {(selectedContact.pickupInstructions ||
              (showDeliveryInstructions &&
                selectedContact.deliveryInstructions)) && (
              <div className="text-xs text-muted-foreground pt-2 border-t space-y-1">
                {selectedContact.pickupInstructions && (
                  <div>
                    <strong>Pickup:</strong>{" "}
                    {selectedContact.pickupInstructions}
                  </div>
                )}
                {showDeliveryInstructions &&
                  selectedContact.deliveryInstructions && (
                    <div>
                      <strong>Delivery:</strong>{" "}
                      {selectedContact.deliveryInstructions}
                    </div>
                  )}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
